// card-5.twig

import {
  Asset,
  Assets,
  Content,
  ContentOptions,
  GridOptions,
  Headline,
  HeadlineOptions,
  LazyImageAdapter,
  LinkOptions,
} from '@miro-site/components_utils'

import {
  Arrow,
  Box,
  Button,
  Card,
  Centered,
  Container,
  Grid,
  GridColumn,
  Link,
  Section,
} from '@miro-site/core'

import React, {MouseEventHandler, useEffect, useRef, useState} from 'react'
import styled, {css, useTheme} from 'styled-components'

interface Item {
  link: LinkOptions
  image?: Assets | Asset
  headline?: HeadlineOptions
  content?: ContentOptions
  arrow?: boolean
  onCardClick?: (url: string) => boolean
  label?: ContentOptions
}

interface ContentLoader {
  total: number
  loader: (offset: number) => Promise<Item[]>
}

interface RelatedContentProps {
  headline?: HeadlineOptions
  items: Item[]
  grid?: GridOptions
  arrow?: boolean
  onCardClick?: (url: string) => boolean
  className?: string
  contentLoader?: ContentLoader
  sectionProps?: React.ComponentProps<typeof Section>
}

const DEFAULT_COLUMNS = 3

const IconWrapper = styled(Box).attrs({
  $styles: {
    width: 40,
    height: 40,
    right: 16,
    bottom: 16,
  },
})`
  display: flex;
  align-items: center;
  justify-content: center;

  transition: background ${({theme}) => theme.animation.transitionDuration};
  background: ${({theme}) => theme.colors.primaryBlue};
  border-radius: 50%;
  position: absolute;
`

const StyledCard = styled(Card)<React.ComponentProps<typeof Card> & {isFocused: boolean}>`
  cursor: pointer;
  overflow: hidden;

  &:hover {
    ${IconWrapper} {
      background: ${({theme}) => theme.colors.primaryBlueHover};
    }
  }
`

export const Item = (props: Item): React.ReactElement => {
  const {image, headline, link, content, arrow, onCardClick, label, ...restProps} = props

  const theme = useTheme()

  const isAnchorLink: boolean = typeof link.href !== 'undefined' && link.href.indexOf('#') === 0

  const [isFocused, setFocused] = useState(false)

  const linkRef = useRef<HTMLAnchorElement>(null)

  const handleItemClick: MouseEventHandler<HTMLElement> = e => {
    const selection = document.getSelection()

    if (selection?.toString()) {
      return
    }

    let isLinkOpenHandled = false

    if (onCardClick) {
      isLinkOpenHandled = onCardClick(link.href)
    }

    if (!isLinkOpenHandled) {
      e.preventDefault()

      if (!isAnchorLink) {
        window.open(link.href, link.href.startsWith('https://miro.com/') ? '_self' : '_blank')
      } else {
        const section = document.getElementById(link.href)

        window.location.href = link.href

        if (section) {
          section.scrollIntoView({behavior: 'smooth'})
        }
      }
    }
  }

  return (
    <StyledCard
      as="article"
      $styles={{
        height: '100%',
        background: theme.colors.primaryWhite,
        position: 'relative',
        paddingBottom: arrow ? 80 : 32,
      }}
      onClick={(e: React.MouseEvent<HTMLElement>) => {
        if (!linkRef.current?.contains(e.target as Node)) {
          handleItemClick(e)
        }
      }}
      $isFocused={isFocused}
      data-testid="related-content-card"
      {...restProps}
    >
      {image && <LazyImageAdapter assets={image} />}
      {headline && (
        <Box $px={[20, 24]} $pt={image ? theme.spaces.lg : theme.spaces.md}>
          {label && (
            <Content
              color="dark"
              size="tiny"
              colorTransparency={0.4}
              textProps={{
                $styles: {
                  marginBottom: [12],
                  letterSpacing: 0.5,
                  fontWeight: 500,
                },
              }}
              {...label}
            />
          )}

          {headline?.text && (
            <Headline
              text={
                <Link
                  ref={linkRef}
                  href={link.href}
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    handleItemClick(e)
                  }}
                  onFocus={e => {
                    if (e.target === linkRef.current) {
                      setFocused(true)
                    }
                  }}
                  onBlur={() => setFocused(false)}
                >
                  {headline.text}
                </Link>
              }
              align="left"
              size="tiny"
              tag="h3"
            />
          )}
          {content && (
            <Content
              color="grey"
              textProps={{
                $styles: {
                  marginTop: headline.text ? [theme.spaces.sm, theme.spaces.md] : 0,
                },
              }}
              {...content}
            />
          )}
        </Box>
      )}
      {arrow && (
        <IconWrapper>
          <Arrow
            aria-hidden="true"
            direction={isAnchorLink ? 'bottom' : 'right'}
            width={[13, 15]}
            height={[10, 12]}
          />
        </IconWrapper>
      )}
    </StyledCard>
  )
}

const StyledGrid = styled(Grid)`
  ${GridColumn} + ${GridColumn} {
    ${({theme}) =>
      theme.normalizeStyles({
        marginTop: [16, null],
      })}
  }

  ${({columns = DEFAULT_COLUMNS}) => css`
    ${GridColumn}:nth-child(n+${columns + 1}) {
      ${({theme}) =>
        theme.normalizeStyles({
          marginTop: [null, 20],
        })}
    }
  `}
`

const RelatedContent: React.FC<RelatedContentProps> = ({
  headline,
  items: initialItems = [],
  arrow,
  grid = {
    columns: DEFAULT_COLUMNS,
  },
  onCardClick,
  className,
  contentLoader,
  sectionProps = {},
}) => {
  const theme = useTheme()

  const [isLoading, setLoading] = useState(false)

  const [items, setItems] = useState<Item[]>(initialItems)

  useEffect(() => {
    if (!contentLoader) {
      return
    }

    const {loader} = contentLoader

    if (isLoading) {
      loadContent()
    }

    async function loadContent() {
      const loadedItems = await loader(items.length)

      setItems(prev => [...prev, ...loadedItems])
    }
  }, [isLoading, contentLoader])

  return (
    <Section data-place="Related Content" type="large" {...sectionProps} className={className}>
      <Container>
        {headline?.text && (
          <Headline
            {...headline}
            size={headline.size || 'mid'}
            tag={headline.tag || 'h2'}
            align={headline.align || 'center'}
          />
        )}
        {items?.length > 0 && (
          <Box $styles={headline && {marginTop: [theme.spaces.xl]}}>
            <StyledGrid columns={grid.columns} align="stretch">
              {items.map((item, index) => (
                <Item key={index} arrow={arrow} onCardClick={onCardClick} {...item} />
              ))}
            </StyledGrid>
          </Box>
        )}
        {contentLoader && items.length < contentLoader.total && (
          <Centered $mt={theme.spaces.xl}>
            <Button
              $buttonType="primary"
              $size="medium"
              isLoading={isLoading}
              onClick={() => setLoading(!isLoading)}
            >
              Show More
            </Button>
          </Centered>
        )}
      </Container>
    </Section>
  )
}

export default RelatedContent
